<template>
  <div :class="computedClass">
    <p class="instrument-price__text--primary">
      <component
        :is="textComponent"
        :class="computedMoneyClass"
        :width="priceWidth"
      >
        {{ price?.formatted }}
      </component>
    </p>

    <p
      v-if="percentual"
      data-testid="instrument-price-percentual"
      :class="computedPercentualClass"
    >
      <component
        :is="textComponent"
        :class="computedMoneyClass"
        :width="percentualWidth"
      >
        {{ percentual?.formatted }}
      </component>
    </p>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop } from 'vue-property-decorator';

import { Money } from '@/modules/common/components/MoneyV2';

import { NumberFormatted } from '@/modules/trade/types';

export enum InstrumentPriceSizes {
  SMALL = 'small',
  REGULAR = 'regular'
}

@Component({
  components: {
    Money
  }
})
export default class InstrumentPrice extends Vue {
  @Prop({ type: Object, required: true })
  readonly price!: NumberFormatted;

  @Prop({ type: String, default: '120px' })
  readonly priceWidth!: string;

  @Prop({ type: Object, required: false })
  readonly percentual?: NumberFormatted;

  @Prop({ type: String, default: '80px' })
  readonly percentualWidth!: string;

  @Prop({ type: String, default: InstrumentPriceSizes.REGULAR })
  readonly size!: InstrumentPriceSizes;

  @Prop({ type: Boolean, default: false })
  readonly enableToggleMoney!: boolean;

  @Prop({ type: String, default: 'left' })
  readonly align!: string;

  @Prop({ type: Boolean, default: false })
  readonly allowNeutralColor!: boolean;

  get computedClass() {
    return ['instrument-price', `instrument-price--${this.size}`];
  }

  get textComponent() {
    return this.enableToggleMoney ? 'Money' : 'span';
  }

  get computedPercentualClass() {
    const percentualFormatted = this.percentual!.formatted;
    const hasNonZeroDigits = /[1-9]/.test(percentualFormatted);

    return [
      'instrument-price__text--secondary',
      {
        'instrument-price__text--positive':
          this.percentual && this.percentual.value > 0,
        'instrument-price__text--negative':
          this.percentual && this.percentual.value < 0,
        'instrument-price__text--neutral':
          !hasNonZeroDigits && this.allowNeutralColor
      }
    ];
  }

  get computedMoneyClass() {
    return [
      'instrument-price__money',
      {
        'instrument-price__money--left': this.align === 'left',
        'instrument-price__money--right': this.align === 'right'
      }
    ];
  }
}
</script>

<style lang="less" scoped>
.instrument-price {
  &__money {
    &--left {
      margin-right: auto;
    }
    &--right {
      margin-left: auto;
    }
  }

  &__text {
    &--primary {
      color: @text-primary;
      margin-left: auto;
    }
    &--secondary {
      color: @text-secondary;
      margin-left: auto;
    }
    &--positive {
      color: @monetary-positive;
    }
    &--negative {
      color: @monetary-negative;
    }
    &--neutral {
      color: @element-primary;
    }
  }

  &--small {
    .instrument-price {
      &__text {
        &--primary {
          .text-p-5();
        }
        &--secondary {
          .text-p-6();
        }
      }
    }
  }

  &--regular {
    .instrument-price {
      &__text {
        &--primary {
          .text-p-3();
        }
        &--secondary {
          .text-p-5();
        }
      }
    }
  }
}
</style>
